'use client';

import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import FeedbackDialog from './FeedBackDialogue';
import { Sparkles } from 'lucide-react';
import InspirationDialogue from '@/components/inspiration-dialogue';
import QuickGuideDialogue from '@/components/QuickGuide';
import { chatLinks } from '@/constants';
import { LogoLarge } from '@/components/common';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCallback, useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { searchVideos } from '@/lib/api/video';
import debounce from 'lodash.debounce';
import { useRef } from 'react';
import { Book } from 'lucide-react';
import { BookOpenCheck } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import CommonNav from '@/components/layouts/CommonNav';
import AppFooter from '@/components/layouts/AppFooter';
import { LogIn } from 'lucide-react';
import { User } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const links = [
  {
    title: 'View all categories?',
    url: 'chat?exclude=false',
    disabled: false,
    category: undefined,
  },
  {
    title: 'Ready to deepen your faith?',
    url: '/chat?category=faith',
    disabled: false,
    category: 'faith',
  },
  {
    title: 'Feeling anxious or depressed?',
    url: '/chat?category=emotions',
    disabled: false,
    category: undefined,
  },
  { title: 'Are you a teen?', url: '/chat?category=teen', disabled: false, category: 'teen' },
  {
    title: 'Want a free personality test?',
    url: 'https://mindbalance.net/assessment/self',
    disabled: false,
    target: '_blank',
    category: undefined,
  },
  {
    title: 'Craving tips for entrepreneurs?',
    url: 'chat?category=entrepreneurs',
    disabled: false,
    category: 'entrepreneurs',
  },
  {
    title: 'Need a spark of inspiration?',
    url: '/chat?category=quotes',
    disabled: false,
    category: 'quotes',
  },
  { title: 'Planning a trip?', url: 'chat?category=travel', disabled: false, category: 'travel' },
  { title: 'Seeking  something else?', url: '/chat?exclude=true', disabled: false, category: '' },
];

const pages = [
  { title: 'Personal', url: '/' },
  { title: 'Self Test', url: 'assessment/self' },
  { title: 'Knowledge', url: 'blogs' },
];

export default function Options({ categories }) {
  const router = useRouter();

  const { data: session } = useSession();

  const handleOptionSelect = (link) => {
    if (link.startsWith('https')) {
      // External link, open in new tab
      window.open(link, '_blank');
    } else {
      // Internal link
      router.push(link);
    }
  };

  const [openDropdown, setOpenDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [videos, setVideos] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const inputRef = useRef(null);

  const debouncedSearchVideos = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setVideos([]);
        return;
      }

      try {
        const data = await searchVideos(query);

        setVideos(Array.isArray(data) ? data : []);
      } catch {
        setVideos([]);
      } finally {
        setSearching(false);
      }
    }, 500),
    []
  );

  const handleInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.length >= 1) {
      setSearching(true);
      setOpenDropdown(true);
      debouncedSearchVideos(query);
    } else {
      setVideos([]);
      setSearching(false);
      if (query.length === 0) {
        // setOpenDropdown(false)
      }
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;

    const months = Math.floor(days / 30);
    if (months < 12) {
      if (months === 1) return '1 month ago';
      return `${months} months ago`;
    }

    // For dates older than 1 year, you can choose either format:
    // Option 1: Return "X years ago"
    const years = Math.floor(months / 12);
    if (years === 1) return '1 year ago';
    return `${years} years ago`;

    // Option 2: Return formatted date string for very old dates
    // return date.toLocaleDateString();
  };

  const formatViews = (number) => {
    if (number < 1000) return number.toString();
    if (number < 1_000_000) return (number / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    if (number < 1_000_000_000) return (number / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    return (number / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  };

  return (
    <>
      <ScrollArea className="overflow-y-auto">
        <main className="flex min-h-screen w-full flex-col items-center overflow-y-auto bg-white">
          <div className="mb-5 px-8 pt-5">
            <div className="space-y-2">
              <div className="relative mt-8 w-full">
                <div className="group relative flex items-center transition-all duration-300 hover:scale-[1.01]">
                  <Search
                    className="text-primary absolute top-1/2 left-4 z-20 h-4 w-4 -translate-y-1/2 transform transition-colors duration-300"
                    aria-hidden="true"
                  />
                  <Input
                    type="text"
                    placeholder="Search video topics…"
                    className="focus-visible:ring-dark-blue z-10 w-full rounded-full py-6 pr-12 pl-11 text-lg focus-visible:ring-1"
                    onChange={handleInputChange}
                  />
                </div>

                <Popover open={openDropdown} onOpenChange={setOpenDropdown}>
                  <PopoverTrigger asChild>
                    <div />
                  </PopoverTrigger>

                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    className="mx-1 w-xs rounded-sm border-0 bg-white p-0 shadow-lg sm:w-[420px]"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <ScrollArea
                      className={`${videos.length <= 5 ? 'h-max' : 'h-[350px] sm:h-[400px]'} px-2 py-4`}
                    >
                      <>
                        {searchQuery &&
                          !searching &&
                          videos.length > 0 &&
                          videos.map((video, index) => (
                            <div
                              key={index}
                              className="text-primary flex cursor-pointer justify-start gap-2 px-4 py-2 hover:bg-gray-200"
                              onClick={() =>
                                handleOptionSelect(
                                  `/chat?category=${video?.videoCategory?.slug}&subCategory=${video?.videoSubCategory?.slug}&videoSlug=${video?.slug}`
                                )
                              }
                            >
                              {video.thumbnailUrl && (
                                <div className="relative h-[80px] w-[120px] flex-shrink-0 overflow-hidden rounded-[4px]">
                                  <Image
                                    src={`${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${video.thumbnailUrl}`}
                                    width={130}
                                    height={80}
                                    alt=""
                                    priority="lazy"
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              )}

                              <div className="flex flex-1 flex-col gap-1">
                                <div
                                  className="line-clamp-2 text-[.9rem] font-semibold"
                                  style={{ lineHeight: '18px' }}
                                >
                                  {video.title}
                                </div>
                                <div className="text-[.8rem] italic">
                                  {video?.videoCategory?.title}
                                </div>
                                <div className="text-[.65rem]">
                                  <span className="me-2 text-blue-800">
                                    {formatViews(video.views)} views.{' '}
                                  </span>
                                  <span>{formatTime(video.youtubePublishedAt)}</span>
                                </div>
                              </div>
                            </div>
                          ))}

                        {searchQuery.length >= 1 && !searching && videos.length === 0 && (
                          <p>No videos found. Try searching with different keywords.</p>
                        )}

                        {searchQuery && searching && (
                          <div className="space-y-3 px-4 py-2">
                            {[...Array(4)].map((_, i) => (
                              <div key={i} className="flex gap-3">
                                <div className="h-[80px] w-[130px] animate-pulse rounded-sm bg-gray-200" />

                                <div className="flex-1 space-y-2">
                                  <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                                  <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                                  <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
                                  <div className="mt-2 h-2 w-1/3 animate-pulse rounded bg-gray-200" />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="mt-5 flex justify-start ps-3">
                <div className="flex flex-col items-start justify-center gap-y-2">
                  {categories &&
                    categories.length > 0 &&
                    categories.map((category, index) => (
                      <div key={index}>
                        <Button
                          key={index}
                          variant="outline"
                          onClick={() => handleOptionSelect(`/chat?category=${category.slug}`)}
                          className="text-primary hover:bg-periwinkle hover:text-dark-blue hover:border-periwinkle h-10 w-auto max-w-full cursor-pointer rounded-full border-[#98a4c0] px-2 py-2 text-center text-xs break-words sm:py-3 md:text-sm"
                        >
                          <span className="font-bold">{category.title}</span> -{' '}
                          {category.description || 'Explore this category'}
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <div className="relative bottom-6 left-0 z-50 mt-5 flex w-full max-w-sm flex-col justify-start px-12 sm:fixed sm:left-6">
            <div>
              {!session && (
                <Link href={'/signin'} className="">
                  <Button
                    variant="default"
                    className="me-3 mb-2 cursor-pointer rounded-full px-1 text-xs sm:text-[15px]"
                  >
                    <LogIn /> Login
                  </Button>
                </Link>
              )}
            </div>

            {session && (
              <>
                {session?.user?.role === 'consumer' && (
                  <Link href={'/chat'} className="">
                    <Button
                      variant="default"
                      className="me-3 mb-2 cursor-pointer rounded-full px-1 text-xs sm:text-[15px]"
                    >
                      <User /> Profile
                    </Button>
                  </Link>
                )}

              </>
            )}

            <Link href={'/blogs'} className="">
              <Button
                variant="default"
                className="me-3 mb-2 w-auto cursor-pointer rounded-full px-1 text-xs sm:text-[15px]"
              >
                <BookOpenCheck /> Smart Reads
              </Button>
            </Link>
            <div className="w-auto">
              <QuickGuideDialogue />
            </div>
          </div>

          {/* Fixed Feedback Button - Bottom Right Corner */}
          <div className="w-full max-w-sm">
            <div className="relative bottom-6 z-50 flex flex-col px-12 sm:fixed sm:right-6">
              <div className="w-auto">
                {session && <InspirationDialogue />}
                <FeedbackDialog />
              </div>
            </div>
          </div>

          {/* <AppFooter /> */}
        </main>
      </ScrollArea>
    </>
  );
}
